import React, { useEffect } from "react";

import styled, { keyframes } from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import { D1, GradientSpan, P1 } from "../../../components/theme/text/Headings";
import {
  Blue,
  Pink,
  White,
  White07,
  gradientColors,
} from "../../../components/theme/colors";
import { ButtonGradient } from "../../../components/theme/Button";
import { useNavigate } from "react-router-dom";
import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";
import LayoutOnboarding from "../../../components/layout/layoutOnboarding";
import { useAuth } from "../../../services/AuthContext";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const HeroSection = styled.div`
  margin-top: 12vh;
  padding: 0 15px;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroCopy = styled(D1)`
  font-size: 32px;
  line-height: 1.25;
  color: ${White};
  margin: 0;
`;

const IllustrationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
  animation: ${fadeInUp} 0.9s ease-out 0.1s both;
`;

const Blob = styled.div`
  width: 220px;
  height: 160px;
  border-radius: 50% 50% 45% 45%;
  background: rgba(255, 255, 255, 0.08);
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 25px;
  box-shadow: inset 0 10px 30px rgba(255, 255, 255, 0.08);
`;

const Eyes = styled.div`
  display: flex;
  gap: 12px;
`;

const Eye = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${White};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #000;
  }
`;

const CTASection = styled.div`
  margin-top: 60px;
  animation: ${fadeInUp} 1s ease-out 0.2s both;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const OnboardingIntro = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "intro",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Intro Viewed", props);
    postHogService.track("Onboarding Intro Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    navigate("/onboarding-one");
  };

  return (
    <LayoutOnboarding>
      <ProgressBarContainer>
        <ProgressBarProgress
          $background={gradientColors["personality-check"].background}
          $transitionBackground={
            gradientColors["personality-check"].transitionBackground
          }
          width={`10%`}
        />
      </ProgressBarContainer>
      <Container>
        <Row>
          <Col
            lg={{ offset: 3, span: 6 }}
            md={{ offset: 2, span: 8 }}
            sm={{ offset: 1, span: 10 }}
            xs={{ span: 12 }}
          >
            <HeroSection>
              <HeroCopy>
                You're{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  unique.
                </GradientSpan>
                <br />
                Your profile should be{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  just as original.
                </GradientSpan>
              </HeroCopy>
              <P1 margin="20px 0 0 0" lineHeight="1.5" color={White07}>
                We’ll make sure every detail reflects your vibe. It only takes a
                minute to get started.
              </P1>
            </HeroSection>
            <IllustrationWrapper>
              <Blob>
                <Eyes>
                  <Eye />
                  <Eye />
                </Eyes>
              </Blob>
            </IllustrationWrapper>
            <CTASection>
              <ButtonGradient
                width="90%"
                className="mt-20"
                $background={gradientColors["personality-check"].background}
                $transitionBackground={
                  gradientColors["personality-check"].transitionBackground
                }
                onClick={handleContinue}
              >
                Set up my profile
              </ButtonGradient>
            </CTASection>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingIntro;
